import { useCallback, useState } from "react";

import { downloadBlob, isBlob, type ExportFormat } from "./download";

/** The slice of an openapi-fetch result an export needs. */
type FetchResult = { data?: unknown; error?: unknown };

/**
 * Fetch a file with the session's auth and save it.
 *
 * The dashboard API authenticates with a bearer header, so a plain `<a href>`
 * to the endpoint would arrive unauthenticated; the file has to be fetched and
 * handed to the browser. `fetcher` makes the request for a format (as a blob);
 * `basename` is the download's name without its extension.
 */
export function useFileExport(
  fetcher: (format: ExportFormat) => Promise<FetchResult>,
  basename: string,
) {
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportAs = useCallback(
    async (format: ExportFormat) => {
      setFailed(false);
      setBusy(true);
      try {
        const { data, error } = await fetcher(format);
        if (error !== undefined || !isBlob(data)) {
          setFailed(true);
          return;
        }
        downloadBlob(data, `${basename}.${format}`);
      } catch {
        setFailed(true);
      } finally {
        setBusy(false);
      }
    },
    [fetcher, basename],
  );

  return { exportAs, failed, busy };
}
